import type { ConnectCardRecord } from './types';

// The `connect_account_v1` content (CC §4.1), read defensively: the card draws it, and the page's
// listener asks it whether a pending service was offered a Reconnect.

const SERVICE_ID = /^[a-z][a-z0-9_]*$/;

export interface Offered {
  service: string;
  name: string;
  state: 'connect' | 'reconnect';
}

export interface Unavailable {
  service: string;
  name: string;
  reason: string;
}

export interface ConnectContent {
  state: 'connect' | 'reconnect' | 'unavailable';
  services: Offered[];
  unavailable: Unavailable[];
  query: string;
  maxResults?: number;
  afterConnectedContent: boolean;
}

function entries(raw: unknown): Record<string, unknown>[] {
  return Array.isArray(raw)
    ? raw.filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
    : [];
}

function serviceOf(e: Record<string, unknown>): { service: string; name: string } | null {
  if (typeof e.service !== 'string' || !SERVICE_ID.test(e.service)) return null;
  return { service: e.service, name: typeof e.name === 'string' && e.name ? e.name : e.service };
}

export function readConnectContent(content: Record<string, unknown>): ConnectContent {
  const max = content.max_results;
  return {
    state:
      content.state === 'unavailable'
        ? 'unavailable'
        : content.state === 'reconnect'
          ? 'reconnect'
          : 'connect',
    services: entries(content.services).flatMap((e): Offered[] => {
      const s = serviceOf(e);
      return s ? [{ ...s, state: e.state === 'reconnect' ? 'reconnect' : 'connect' }] : [];
    }),
    unavailable: entries(content.unavailable).flatMap((e): Unavailable[] => {
      const s = serviceOf(e);
      return s ? [{ ...s, reason: typeof e.reason === 'string' ? e.reason : '' }] : [];
    }),
    query: typeof content.query === 'string' ? content.query : '',
    ...(typeof max === 'number' && Number.isInteger(max) && max > 0 ? { maxResults: max } : {}),
    afterConnectedContent: content.after_connected_content === true,
  };
}

/** The card offers the service a Reconnect. */
export function offersReconnect(card: ConnectCardRecord, service: string): boolean {
  return readConnectContent(card.proposal.content).services.some(
    (s) => s.service === service && s.state === 'reconnect',
  );
}
